import { Card, Text, Button, Badge, BlockStack, List } from '@shopify/polaris';
import { useLanguage } from '../providers/LanguageProvider';
import type { SubscriptionPlan } from '../types';
import { PlanTier } from '../types';

interface PlanCardProps {
  plan: SubscriptionPlan;
  currentPlanTier?: PlanTier;
  onSubscribe: (planTier: PlanTier) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Try-ons the trial allows, shown alongside the trial badge */
  trialTryOnCap?: number;
}

/** Prices are quoted in USD on every locale, so format them the same way everywhere */
function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export default function PlanCard({
  plan,
  currentPlanTier,
  onSubscribe,
  loading = false,
  disabled = false,
  trialTryOnCap,
}: PlanCardProps) {
  const { t } = useLanguage();

  const isFree = plan.id === PlanTier.FREE;
  const isCurrentPlan = currentPlanTier === plan.id;
  const isUpgrade =
    currentPlanTier && plan.sortOrder > getPlanSortOrder(currentPlanTier);
  const isDowngrade =
    currentPlanTier && plan.sortOrder < getPlanSortOrder(currentPlanTier);

  const buttonText = isCurrentPlan
    ? t.planCard.currentPlan
    : isFree
    ? t.planCard.startFree
    : isUpgrade
    ? t.planCard.upgrade
    : isDowngrade
    ? t.planCard.downgrade
    : t.planCard.subscribe;

  const trialDays = plan.trialDays ?? 0;

  return (
    <Card>
      <BlockStack gap="400">
        {/* Plan Header */}
        <BlockStack gap="200">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text as="h2" variant="headingLg">
              {plan.name}
            </Text>
            {isCurrentPlan ? (
              <Badge tone="success">{t.planCard.active}</Badge>
            ) : isFree ? (
              <Badge tone="info">{t.planCard.freeBadge}</Badge>
            ) : null}
          </div>
          <Text as="p" variant="bodySm" tone="subdued">
            {plan.description}
          </Text>
        </BlockStack>

        {/* Pricing */}
        <BlockStack gap="100">
          <Text as="p" variant="heading2xl">
            {isFree ? t.planCard.freePrice : `${formatUsd(plan.price)} USD`}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {isFree ? t.planCard.noCardRequired : t.planCard.perMonth}
          </Text>
          {trialDays > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <Badge tone="success">{t.planCard.trialBadge(trialDays)}</Badge>
            </div>
          )}
          {trialDays > 0 && trialTryOnCap !== undefined && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t.planCard.trialCapNote(trialTryOnCap)}
            </Text>
          )}
          {trialDays > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t.planCard.chargedEvery(30)}
            </Text>
          )}
        </BlockStack>

        {/* Included Try-ons, and what happens once they run out */}
        <BlockStack gap="100">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {t.planCard.tryOnsIncluded(plan.includedTryOns)}
          </Text>
          {plan.overage ? (
            <>
              <Text as="p" variant="bodySm">
                {t.planCard.overageLine(formatUsd(plan.overage.pricePerTryOn))}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t.planCard.overageCapNote(formatUsd(plan.overage.defaultCappedAmount))}
              </Text>
            </>
          ) : (
            <Text as="p" variant="bodySm" tone="subdued">
              {t.planCard.freeStopsAtLimit}
            </Text>
          )}
        </BlockStack>

        {/* Features */}
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {t.planCard.features}
          </Text>
          <List type="bullet">
            {plan.features.map((feature, index) => (
              <List.Item key={index}>{feature}</List.Item>
            ))}
          </List>
        </BlockStack>

        {/* Action Button */}
        <Button
          variant={(!currentPlanTier && !isFree) || isUpgrade ? 'primary' : 'secondary'}
          disabled={isCurrentPlan || loading || disabled}
          onClick={() => onSubscribe(plan.id)}
          fullWidth
          loading={loading}
        >
          {buttonText}
        </Button>
      </BlockStack>
    </Card>
  );
}

// Helper to get plan sort order
function getPlanSortOrder(tier: PlanTier): number {
  const orders: Record<PlanTier, number> = {
    FREE: 0,
    STARTER: 1,
    GROWTH: 2,
    BUSINESS: 3,
  };
  return orders[tier] || 0;
}
